/**
 * Provenance d'une relation (C22 SP1 « Provenance », spec § 2.1).
 *
 * Une marque de concept sur un passage, une référence `[[…]]` et un renvoi
 * SKOS partagent le même QUADRUPLET : qui l'a posée (`auteurId`), quand
 * (`poseeLe`), comment (`origine`) et si elle est validée (`validated`).
 * Le serveur estampille `auteurId` / `poseeLe` à la pose et ne les réécrit
 * jamais ; les données antérieures à SP1 n'en portent pas.
 *
 * Ce module LIT de la donnée JSON brute (item d'un nœud `entrees`, sous-document
 * Mongoose, réponse HTTP) : chaque lecteur est tolérant à tout type et ne lève
 * jamais. Sur une marque, l'origine n'est pas stockée : elle se DÉDUIT de
 * `detectionMethod` (`origineDeMarque`) ; `validated` se lit toujours par
 * `estValidee`, jamais par `item.validated === true` nu, pour qu'une marque
 * posée à la main avant SP1 compte validée quel que soit l'écran qui l'a posée.
 *
 * Ce fichier vit dans src/utils/ et est COPIÉ octet pour octet dans
 * api/src/utils/ (test de dérive : api/src/utils/provenance.drift.test.ts).
 * Modifier ici, puis recopier. Aucun import : la copie serveur doit rester autonome.
 */

/** Comment la relation est née : à la main, par le repérage, par l'assistant, par un import. */
export type Origine = 'manuelle' | 'reperage' | 'assistant' | 'import';

export interface Provenance {
  origine: Origine;
  /** Identifiant de la personne qui a posé la relation. Absent : antérieur à SP1. */
  auteurId?: string;
  /** Date de pose, ISO 8601. Absente : antérieure à SP1. Jamais réécrite. */
  poseeLe?: string;
  validated: boolean;
}

/** Les champs d'une relation tels qu'ils arrivent du JSON : tout est inconnu. */
export interface ChampsProvenance {
  detectionMethod?: unknown;
  origine?: unknown;
  validated?: unknown;
  auteurId?: unknown;
  poseeLe?: unknown;
}

const ORIGINES: ReadonlyArray<Origine> = ['manuelle', 'reperage', 'assistant', 'import'];

const estOrigine = (valeur: unknown): valeur is Origine =>
  typeof valeur === 'string' && (ORIGINES as ReadonlyArray<string>).includes(valeur);

/** Une chaîne non vide ; `''` compte comme absente (une date ou un auteur vides ne datent ni ne signent rien). */
const chaine = (valeur: unknown): string | undefined => (typeof valeur === 'string' && valeur !== '' ? valeur : undefined);

/** Une chaîne ISO telle quelle, une `Date` (Mongoose) valide rendue en ISO, toute autre valeur absente. */
const dateIso = (valeur: unknown): string | undefined => {
  if (typeof valeur === 'string') return chaine(valeur);
  if (valeur instanceof Date && !Number.isNaN(valeur.getTime())) return valeur.toISOString();
  return undefined;
};

/**
 * L'origine d'une marque, déduite de `detectionMethod` : `'manual'` ou absent →
 * manuelle (les données anciennes du bureau n'ont pas de méthode) ; `'rag-write'`
 * → assistant ; `'import'` → import ; toute autre valeur (nom de détecteur, ancien
 * ou nouveau) → repérage.
 */
export function origineDeMarque(item: ChampsProvenance): Origine {
  const methode = item.detectionMethod;
  if (methode === undefined || methode === null || methode === 'manual') return 'manuelle';
  if (methode === 'rag-write') return 'assistant';
  if (methode === 'import') return 'import';
  return 'reperage';
}

/**
 * Lecture tolérante de la validation d'une marque : `validated === true`, OU une
 * marque sans date de pose (antérieure à SP1) d'origine manuelle ou importée —
 * une personne l'a posée, elle est validée par définition, même si l'écran de
 * l'époque a écrit `false`.
 */
export function estValidee(item: ChampsProvenance): boolean {
  if (item.validated === true) return true;
  if (chaine(item.poseeLe) !== undefined) return false;
  const origine = origineDeMarque(item);
  return origine === 'manuelle' || origine === 'import';
}

/** Le quadruplet d'une marque ; `auteurId` et `poseeLe` repris tels quels s'ils sont des chaînes. */
export function provenanceDeMarque(item: ChampsProvenance): Provenance {
  const provenance: Provenance = { origine: origineDeMarque(item), validated: estValidee(item) };
  const auteurId = chaine(item.auteurId);
  const poseeLe = chaine(item.poseeLe);
  if (auteurId !== undefined) provenance.auteurId = auteurId;
  if (poseeLe !== undefined) provenance.poseeLe = poseeLe;
  return provenance;
}

/**
 * Le quadruplet d'une référence `[[…]]` ou d'un renvoi SKOS : `origine` lue si
 * elle est une des quatre valeurs, sinon manuelle ; `validated` lue si booléenne,
 * sinon vraie (antérieur à SP1 : posé par une personne) ; `poseeLe` accepte une
 * chaîne ISO ou une `Date` (Mongoose) et rend toujours une chaîne.
 */
export function provenanceDe(objet: ChampsProvenance): Provenance {
  const provenance: Provenance = {
    origine: estOrigine(objet.origine) ? objet.origine : 'manuelle',
    validated: typeof objet.validated === 'boolean' ? objet.validated : true,
  };
  const auteurId = chaine(objet.auteurId);
  const poseeLe = dateIso(objet.poseeLe);
  if (auteurId !== undefined) provenance.auteurId = auteurId;
  if (poseeLe !== undefined) provenance.poseeLe = poseeLe;
  return provenance;
}

/** L'estampille du serveur à la pose : l'écrivain et maintenant, en ISO. */
export function estampille(auteurId: string, maintenant: Date): { auteurId: string; poseeLe: string } {
  return { auteurId, poseeLe: maintenant.toISOString() };
}
